import type { CustomAttributeData } from '@risksmart-app/domain/src/types/common.types';
import type {
  Risk_Treatment_Type_Enum,
  UpdateEnterpriseRiskMutation,
  UpdateEnterpriseRiskMutationVariables,
} from '@risksmart-app/web-graphql-client/generated/graphql';
import {
  namedOperations,
  UpdateEnterpriseRiskDocument,
} from '@risksmart-app/web-graphql-client/generated/graphql';

import { createMutationHook } from '@/utils/hook-factories';

// The `Tier`-discriminated procedure input is built from the flat GraphQL
// variables (see the insert hook).
type UpdateEnterpriseRiskTRPCInput = {
  Id: string;
  Title: string;
  Description: string;
  Treatment: Risk_Treatment_Type_Enum | null;
  CustomAttributeData: CustomAttributeData | null;
} & (
  | { Tier: 1; ParentId: null }
  | { Tier: 2; ParentId: string }
  | { Tier: 3; ParentId: string }
);

export const useUpdateEnterpriseRisk = createMutationHook({
  name: 'updateEnterpriseRisk',
  mutationOptions: (trpc, onSuccess) =>
    trpc.frontend.enterpriseRisk.update.mutationOptions({ onSuccess }),
  mapInput: (
    variables: UpdateEnterpriseRiskMutationVariables
  ): UpdateEnterpriseRiskTRPCInput => {
    const base = {
      Id: variables.Id,
      Title: variables.Title,
      Description: variables.Description ?? '',
      Treatment: variables.Treatment ?? null,
      CustomAttributeData: variables.CustomAttributeData ?? null,
    };

    if (variables.Tier === 1) {
      return { ...base, Tier: 1, ParentId: null };
    }

    if (!variables.ParentId) {
      throw new Error(
        'A parent enterprise risk is required for tier 2 and tier 3 risks'
      );
    }

    if (variables.Tier === 2) {
      return { ...base, Tier: 2, ParentId: variables.ParentId };
    }

    if (variables.Tier === 3) {
      return { ...base, Tier: 3, ParentId: variables.ParentId };
    }

    throw new Error(`Unsupported enterprise risk tier: ${variables.Tier}`);
  },
  invalidate: (trpc) => [
    trpc.frontend.enterpriseRisk.register.queryKey(),
    trpc.frontend.enterpriseRisk.getById.queryKey(),
    trpc.frontend.enterpriseRisk.getByTier.queryKey(),
  ],
  // The tRPC update returns the full row; the V2 GraphQL mutation reported a
  // single affected row, so mirror that shape.
  mapResponse: (): UpdateEnterpriseRiskMutation => ({
    updateChildEnterpriseRisk: {
      __typename: 'GenericMutationOutput',
      affected_rows: 1,
    },
  }),
  graphqlDocument: UpdateEnterpriseRiskDocument,
  graphqlVariables: (variables: UpdateEnterpriseRiskMutationVariables) => ({
    ...variables,
  }),
  evictFields: ['enterprise_risk'],
  refetchQueries: [
    namedOperations.Query.getEnterpriseRiskById,
    namedOperations.Query.getEnterpriseRisks,
  ],
});
